/**
 * The keys are relative leaflet map zoom levels and the values are bloom radius ranges in pixels.
 * The more data points are displayed in a heatmap the smaller the radius should be for a
 * good looking map. Here is a basic linear algorithm for this purpose. The zoom level here is relative to
 * the initial level and it's calculated by currentZoom-initialZoom.
 */
// IMPORTANT: All these radii look good for a map with a width of 940px and height of 400px.
// TODO: The actual radii should be modified proportionally when the map dimension differs from 940x400
const dataSizeAndZoomToRadiusMaps = {
  // the smaller the zoom level the further we're from the planet
  // The following pixel ranges are a result of a LOT OF experiments trying to make each kind of geo
  // regardless of its size and data points look good. However, one size does not fit all: there are
  // still some less appealing geos that call for a custom treatment (e.g. Hawaii)

  '<600': {
    '-10': [2, 6],
    '-9': [2, 6],
    '-8': [2, 6],
    '-7': [2, 6],
    '-6': [2, 6],
    '-5': [2, 6],
    '-4': [2, 6],
    '-3': [4, 10],
    '-2': [9, 15],
    '-1': [18, 24],
    '0': [32, 35],
    // "0": [28, 35],
    '1': [35, 50],
    '2': [45, 55],
    '3': [55, 65],
    '4': [65, 75],
    '5': [75, 85],
    '6': [75, 85],
    '7': [75, 85],
    '8': [75, 85],
    '9': [75, 85],
    '10': [75, 85],
    min: 80,
    max: 600
  },

  '600-1200': {
    '-10': [1, 3],
    '-9': [1, 3],
    '-8': [1, 3],
    '-7': [1, 3],
    '-6': [1, 3],
    '-5': [1, 3],
    '-4': [1, 3],
    '-3': [3, 6],
    '-2': [7, 9],
    '-1': [15, 18],
    '0': [25, 30],
    '1': [35, 42],
    '2': [45, 50],
    '3': [55, 60],
    '4': [65, 75],
    '5': [65, 75],
    '6': [65, 75],
    '7': [65, 75],
    '8': [65, 75],
    '9': [65, 75],
    '10': [65, 75],
    min: 600,
    max: 1200
  },

  '1200-2000': {
    '-10': [3, 5],
    '-9': [3, 5],
    '-8': [3, 5],
    '-7': [3, 5],
    '-6': [3, 5],
    '-5': [3, 5],
    '-4': [3, 5],
    '-3': [3, 5],
    '-2': [5, 8],
    '-1': [10, 15],
    '0': [21, 24],
    '1': [30, 36],
    '2': [40, 45],
    '3': [60, 70],
    '4': [80, 90],
    '5': [80, 90],
    '6': [80, 90],
    '7': [80, 90],
    '8': [80, 90],
    '9': [80, 90],
    '10': [80, 90],
    min: 1200,
    max: 2000
  },

  '2000-3000': {
    '-10': [1, 3],
    '-9': [1, 3],
    '-8': [1, 3],
    '-7': [1, 3],
    '-6': [1, 3],
    '-5': [1, 3],
    '-4': [1, 3],
    '-3': [1, 3],
    '-2': [4, 7],
    '-1': [8, 12],
    '0': [15, 23],
    '1': [25, 32],
    '2': [36, 42],
    '3': [45, 55],
    '4': [65, 75],
    '5': [65, 75],
    '6': [65, 75],
    '7': [65, 75],
    '8': [65, 75],
    '9': [65, 75],
    '10': [65, 75],
    min: 2000,
    max: 3000
  },

  '3000-4000': {
    '-10': [2, 4],
    '-9': [2, 4],
    '-8': [2, 4],
    '-7': [2, 4],
    '-6': [2, 4],
    '-5': [2, 4],
    '-4': [2, 4],
    '-3': [2, 4],
    '-2': [2, 4],
    '-1': [6, 8],
    '0': [14, 16],
    '1': [20, 22],
    '2': [34, 36],
    '3': [42, 44],
    '4': [65, 70],
    '5': [70, 75],
    '6': [70, 75],
    '7': [70, 75],
    '8': [70, 75],
    '9': [70, 75],
    '10': [70, 75],
    min: 3000,
    max: 4000
  },

  '>4000': {
    '-10': [1, 3],
    '-9': [1, 3],
    '-8': [1, 3],
    '-7': [1, 3],
    '-6': [1, 3],
    '-5': [1, 3],
    '-4': [1, 3],
    '-3': [2, 4],
    '-2': [2, 4],
    '-1': [5, 7],
    '0': [13, 15],
    '1': [19, 21],
    '2': [33, 35],
    '3': [41, 43],
    '4': [65, 70],
    '5': [70, 75],
    '6': [70, 75],
    '7': [70, 75],
    '8': [70, 75],
    '9': [70, 75],
    '10': [70, 75],
    min: 4000,
    max: 10000
  }

  // The USA enjoys a unique treatment for best visuals.
  // "Country": { "-3": [15, 25], "-2": [15, 25], "-1": [15, 25], "0": [28, 38], "1": [35, 40], "2": [40, 45], "3": [45, 50], "4": [45, 50], "5": [45, 50], "6": [45, 50], "7": [45, 50], "8": [45, 50], "9": [45, 50], "10": [45, 50]},
}

function zoomToRadii (dataSize) {
  if (dataSize < 600) {
    return dataSizeAndZoomToRadiusMaps['<600']
  } else if (dataSize < 1200) {
    return dataSizeAndZoomToRadiusMaps['600-1200']
  } else if (dataSize < 2000) {
    return dataSizeAndZoomToRadiusMaps['1200-2000']
  } else if (dataSize < 3000) {
    return dataSizeAndZoomToRadiusMaps['2000-3000']
  } else if (dataSize < 4000) {
    return dataSizeAndZoomToRadiusMaps['3000-4000']
  } else {
    return dataSizeAndZoomToRadiusMaps['>4000']
  }
}

// window.z = dataSizeAndZoomToRadiusMaps
export default function radius (zoomLevel, dataSize = 0, mod = 1) {
  const radiiHash = zoomToRadii(dataSize)
  const min = radiiHash[zoomLevel][0]
  const max = radiiHash[zoomLevel][1]
  // function log(r){console.log(`radius: ${r ? r : 1} [min: ${min}, max: ${max}], zoom: ${zoomLevel}, dataSize: ${dataSize}`)}

  // Making the radius smaller regardless of the data size isn't a great idea.
  // This becomes evident for small geos. Setting it to 1 for small data sets.
  mod = dataSize < 150 ? 1 : mod

  let r
  if (dataSize <= 80) {
    r = Math.floor(max * mod)
  } else if (dataSize >= 10000) {
    r = Math.floor(min * mod)
  } else {
    const q = (min - max) / (radiiHash.max - radiiHash.min)
    r = Math.floor((max - (radiiHash.min * q) + (dataSize * q)) * mod)
  }

  // log(r)
  return r || 1
}



// WEBPACK FOOTER //
// ./frontend/src/components/heatmap/bloomRadiusCalculator.js